import * as THREE from 'three';
import type { Chapter, ChapterContext, ScreenLabel } from './types';
import { LETTER } from '../data/letter';
import {
  DEG,
  FIRST_LAP_PHASE,
  S2_A,
  S2_E,
  S2_PERICENTRE,
  S2_PERIOD,
  TWO_PI,
  angularErrorDeg,
  makeSStars,
  pericentreDirection,
  pericentrePhase,
  s2At,
  sStarPosition,
  sampleOrbit
} from '../render/s2/kepler';
import { makeStarField } from '../render/s2/field';
import { TrailBuffer } from '../render/s2/trail';
import {
  glowTexture,
  haloTexture,
  makeStarMaterial,
  makeTrailMaterial,
  reticleTexture,
  ringTexture
} from '../render/s2/sprites';

const meta = LETTER.chapters.find((c) => c.id === 'time')!;

type Stage = 'watch' | 'await' | 'resume' | 'done';

/**
 * camera: orbit around the hole, looking steeply down at the orbit plane. The azimuth sits a quarter turn from
 * the orbit's major axis, plus the lag the follow settles into while the ellipse turns (so it sits level
 * on screen mid-lap and rides a little high while S2 waits), with the hole on the right. The ellipse turns forty degrees a lap and never stops, so
 * the camera follows that turn with a lag: the turn shows against the stars and the old trail, and the orbit
 * never leaves the frame.
 */
const CAM_AZ = -Math.PI / 2 + 0.27;
const FOLLOW_TAU = 6;
const CAM_EL = 0.95;
const CAM_RADIUS = 36;
const EL_MIN = 0.2;
const EL_MAX = 1.45;
const RADIUS_MIN = 12;
const RADIUS_MAX = 80;
/** a slow bounded sway of the azimuth, so the view breathes but never turns the orbit edge-on */
const SWAY_AMPLITUDE = 0.12;
const SWAY_RATE = 0.08;
/** vertical field of view; widened on narrow screens so the width stays the same */
const FOV = 46;
const FULL_FOV_ASPECT = 1.6;
const MAX_FOV = 100;
/**
 * The hole sits at one focus, so the ellipse hangs to one side of it. A projection shift (in NDC, positive is
 * right and up) puts the hole right of centre and above it while the camera still orbits the hole itself,
 * which keeps the whole orbit in frame as it turns.
 */
const VIEW_SHIFT_X = 0.44;
const VIEW_SHIFT_Y = 0.14;

const BH_RADIUS = 0.35;
const STAR_FIELD_COUNT = 1500;
const STAR_FIELD_RADIUS = 120;
const TRAIL_CAPACITY = 200;
const TRAIL_STEP = 0.1;
/** radians of mean anomaly per sub-step while feeding the trail, so a fast pericentre pass leaves no gaps */
const SUBSTEP = 0.0025;
const ORBIT_RATE = TWO_PI / S2_PERIOD;
const ORBIT_SEGMENTS = 256;
const MIN_CLICK_RADIUS = 0.25;
const MAX_MARK_RADIUS = 45;
const PULSE_SECONDS = 1.6;
/** the arrow keys turn the guess mark this far per press, and hold it this far from the hole */
const KEY_STEP = 5 * DEG;
const KEY_MARK_RADIUS = 5;
/** Show me plays the rest of the first lap in steps of this many seconds, the longest step a frame takes */
const SKIP_STEP = 0.1;
const MAX_SKIP_STEPS = 400;
/** mission seconds that pass for every scene second */
const MISSION_SECONDS_PER_SECOND = 3600;

const ICE = '#8cc1de';
const GOLD = '#e3b45a';

/** Colour with HDR gain: values above 1 are what the bloom picks up. */
const hdr = (hex: string, gain: number) => new THREE.Color(hex).multiplyScalar(gain);

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** S2's clock against Earth's: a hair slow when it is close to the hole, level otherwise. */
const clockRate = (r: number) => (r <= 1.5 * S2_PERICENTRE ? 0.9997 : 1);

function additiveSprite(map: THREE.Texture, color: THREE.Color, opts: { opacity?: number; depthTest?: boolean; order?: number } = {}): THREE.Sprite {
  const mat = new THREE.SpriteMaterial({
    map,
    color,
    transparent: true,
    opacity: opts.opacity ?? 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: opts.depthTest ?? true
  });
  const s = new THREE.Sprite(mat);
  s.renderOrder = opts.order ?? 0;
  return s;
}

/**
 * Chapter 3, Time. Star S2 falls past Sagittarius A* every sixteen scene seconds. Its clock runs slow at the
 * closest point, and its orbit does not close: the ellipse turns. The lever is a prediction: after one lap,
 * click where the next closest approach will be, then watch which mark the star lands on.
 */
export class S2Chapter implements Chapter {
  readonly id = 'time';
  readonly title = 'Time';
  readonly wantsClocks = true;
  readonly description = 'The star S2 on its stretched orbit around the black hole Sagittarius A*, seen from above; the two clocks compare your time with Earth’s.';
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV, 16 / 9, 0.1, 500);

  private ctx: ChapterContext | null = null;
  private stage: Stage = 'watch';
  /** cumulative mean anomaly since the scene opened at apocentre; also what turns the ellipse */
  private phase = 0;
  private sTime = 0;
  private pulse = 0;
  private guessDeg: number | null = null;
  /** the keyboard route: where the arrow keys have turned the mark, and whether it is showing yet */
  private keyAngle = pericentreDirection(1);
  private keyPreview = false;

  // camera
  private az = CAM_AZ;
  private el = CAM_EL;
  private radius = CAM_RADIUS;
  private targetRadius = CAM_RADIUS;
  private idle = 0;
  private reduceMotion = false;
  private swayT = 0;
  /** the camera's lagged copy of the argument of periapsis */
  private azFollow = 0;
  private omega = 0;
  private lastW = 0;
  private lastH = 0;

  // pointer unprojection, reused
  private readonly caster = new THREE.Raycaster();
  private readonly plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  private readonly ndc = new THREE.Vector2();
  private readonly hit = new THREE.Vector3();

  // screen labels, reused every frame
  private readonly labelV = new THREE.Vector3();
  private readonly holeLabel: ScreenLabel = { text: 'Sagittarius A*', x: 0, y: 0 };
  private readonly s2Label: ScreenLabel = { text: 'S2', x: 0, y: 0 };
  private readonly labelList: ScreenLabel[] = [];

  // scene objects
  private readonly disc: THREE.Mesh;
  private readonly ring: THREE.Sprite;
  private readonly halo: THREE.Sprite;
  private readonly field: THREE.Points;
  private readonly sStars: THREE.Points;
  private readonly sStarPositions: Float32Array;
  private readonly orbitLine: THREE.LineLoop;
  private readonly trail = new TrailBuffer(TRAIL_CAPACITY, TRAIL_STEP);
  private readonly trailPoints: THREE.Points;
  private readonly s2: THREE.Sprite;
  private readonly guessMark: THREE.Sprite;
  private readonly newtonMark: THREE.Sprite;
  private readonly einsteinMark: THREE.Sprite;
  /** a pulsing dashed ring around the hole while S2 waits: this is where to click */
  private readonly hintRing: THREE.Sprite;
  private readonly sStarData = makeSStars();

  private readonly starMat: THREE.ShaderMaterial;
  private readonly trailMat: THREE.ShaderMaterial;
  private readonly textures: THREE.Texture[] = [];

  constructor() {
    const glow = this.own(glowTexture());
    const halo = this.own(haloTexture());
    const ringTex = this.own(ringTexture({ radius: 0.8, width: 5 }));
    const markTex = this.own(ringTexture({ radius: 0.8, width: 8 }));
    const dashedTex = this.own(ringTexture({ radius: 0.8, width: 10, dash: [20, 14] }));
    const reticle = this.own(reticleTexture());

    // Sagittarius A*: a black disc that hides what is behind it, a thin gold ring, a faint warm halo
    this.disc = new THREE.Mesh(new THREE.CircleGeometry(BH_RADIUS, 64), new THREE.MeshBasicMaterial({ color: 0x000000 }));
    this.disc.renderOrder = -10;
    this.scene.add(this.disc);

    this.halo = additiveSprite(halo, hdr(GOLD, 1), { opacity: 0.35, depthTest: false, order: 1 });
    this.halo.scale.set(4, 4, 1);
    this.scene.add(this.halo);
    this.ring = additiveSprite(ringTex, hdr(GOLD, 2.4), { depthTest: false, order: 2 });
    this.ring.scale.set(1.1, 1.1, 1);
    this.scene.add(this.ring);

    // the star field
    this.starMat = makeStarMaterial(glow);
    const sf = makeStarField(STAR_FIELD_COUNT, STAR_FIELD_RADIUS);
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(sf.positions, 3));
    fieldGeo.setAttribute('aColor', new THREE.BufferAttribute(sf.colors, 3));
    fieldGeo.setAttribute('aSize', new THREE.BufferAttribute(sf.sizes, 1));
    this.field = new THREE.Points(fieldGeo, this.starMat);
    this.field.frustumCulled = false;
    this.field.renderOrder = -5;
    this.scene.add(this.field);

    // the S-star cluster: ten faint stars, each on its own tilted ellipse
    const n = this.sStarData.length;
    this.sStarPositions = new Float32Array(n * 3);
    const sColors = new Float32Array(n * 3), sSizes = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const b = 0.7 + 0.4 * ((i * 0.618034) % 1);
      sColors[i * 3] = 0.85 * b; sColors[i * 3 + 1] = 0.92 * b; sColors[i * 3 + 2] = b;
      sSizes[i] = 10 + 5 * ((i * 0.381966) % 1);
    }
    const sGeo = new THREE.BufferGeometry();
    const sPos = new THREE.BufferAttribute(this.sStarPositions, 3);
    sPos.setUsage(THREE.DynamicDrawUsage);
    sGeo.setAttribute('position', sPos);
    sGeo.setAttribute('aColor', new THREE.BufferAttribute(sColors, 3));
    sGeo.setAttribute('aSize', new THREE.BufferAttribute(sSizes, 1));
    this.sStars = new THREE.Points(sGeo, this.starMat);
    this.sStars.frustumCulled = false;
    this.sStars.renderOrder = 3;
    this.scene.add(this.sStars);

    // S2's orbit, built once in the orbit's own frame and turned with the argument of periapsis
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.BufferAttribute(sampleOrbit(ORBIT_SEGMENTS, S2_A, S2_E, 0), 3));
    this.orbitLine = new THREE.LineLoop(lineGeo, new THREE.LineBasicMaterial({
      color: hdr(ICE, 1), transparent: true, opacity: 0.38, blending: THREE.AdditiveBlending, depthWrite: false
    }));
    this.orbitLine.frustumCulled = false;
    this.orbitLine.renderOrder = 4;
    this.scene.add(this.orbitLine);

    // S2's trail: its last 200 recorded positions, fading toward the tail
    const slots = new Float32Array(TRAIL_CAPACITY);
    for (let i = 0; i < TRAIL_CAPACITY; i++) slots[i] = i;
    const tPos = new THREE.BufferAttribute(this.trail.positions, 3);
    tPos.setUsage(THREE.DynamicDrawUsage);
    const trailGeo = new THREE.BufferGeometry();
    trailGeo.setAttribute('position', tPos);
    trailGeo.setAttribute('aSlot', new THREE.BufferAttribute(slots, 1));
    this.trailMat = makeTrailMaterial(glow, TRAIL_CAPACITY, new THREE.Color(0.55, 0.68, 1.0), 0.34);
    this.trailPoints = new THREE.Points(trailGeo, this.trailMat);
    this.trailPoints.frustumCulled = false;
    this.trailPoints.renderOrder = 5;
    this.scene.add(this.trailPoints);

    // S2 itself: white-blue, bright enough to bloom
    this.s2 = additiveSprite(glow, new THREE.Color(1.6, 1.8, 2.2), { order: 6 });
    this.scene.add(this.s2);

    // marks: the player's guess, Newton's ghost, Einstein's ghost
    this.guessMark = additiveSprite(reticle, hdr(ICE, 1.7), { depthTest: false, order: 7 });
    this.newtonMark = additiveSprite(dashedTex, hdr(ICE, 2.4), { opacity: 1, depthTest: false, order: 7 });
    this.einsteinMark = additiveSprite(markTex, hdr(GOLD, 2.2), { depthTest: false, order: 8 });
    this.hintRing = additiveSprite(dashedTex, hdr(GOLD, 1.2), { opacity: 0.85, depthTest: false, order: 6 });
    for (const m of [this.guessMark, this.newtonMark, this.einsteinMark, this.hintRing]) { m.visible = false; this.scene.add(m); }

    this.camera.up.set(0, 0, 1);
    this.placeCamera();
  }

  /* ---- Chapter ---- */

  enter(ctx: ChapterContext): void {
    this.ctx = ctx;
    this.stage = 'watch';
    this.phase = 0;
    this.sTime = 0;
    this.pulse = 0;
    this.guessDeg = null;
    this.keyAngle = pericentreDirection(1);
    this.keyPreview = false;
    this.trail.reset();
    this.az = CAM_AZ; this.el = CAM_EL; this.radius = CAM_RADIUS; this.targetRadius = CAM_RADIUS;
    this.azFollow = 0; this.omega = 0; this.swayT = 0;
    this.idle = 0;
    this.lastW = 0; this.lastH = 0;
    this.reduceMotion = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.guessMark.visible = this.newtonMark.visible = this.einsteinMark.visible = false;
    this.placeCamera();
    this.syncVisuals(s2At(0));

    ctx.say(meta.intro);
    ctx.hud.setLever({ kind: 'buttons', note: 'Watching one lap', items: [] });
    ctx.hud.setMove(meta.lever);
    ctx.hud.setHint('');
    this.refreshReadouts();
  }

  update(dt: number, _t: number, width: number, height: number): void {
    dt = clamp(dt, 0, 0.1);
    this.updateCamera(dt, width, height);
    const ctx = this.ctx;
    if (!ctx) return;

    const star = this.stepOrbit(dt);
    this.sTime += dt;
    if (this.pulse > 0) this.pulse = Math.max(0, this.pulse - dt);
    this.syncVisuals(star);

    const rate = clockRate(star.r);
    ctx.clock.advance(dt * MISSION_SECONDS_PER_SECOND, dt * MISSION_SECONDS_PER_SECOND * rate);
    ctx.audio.setScene({ rate, dist: 60, phase: 'time', orbitPhase: 0 });
  }

  exit(): void {
    if (!this.ctx) return;
    this.ctx.hud.setLever(null);
    this.ctx.hud.setMove(null);
    this.ctx.hud.setHint('');
    this.ctx.hud.setReadouts([]);
    this.hintRing.visible = false;
  }

  onDrag(dx: number, dy: number): void {
    this.az -= dx * 0.005;
    this.el = clamp(this.el + dy * 0.004, EL_MIN, EL_MAX);
    this.idle = 0;
  }

  onWheel(deltaY: number): void {
    this.targetRadius = clamp(this.targetRadius * (1 + deltaY * 0.0012), RADIUS_MIN, RADIUS_MAX);
    this.idle = 0;
  }

  onClick(x: number, y: number): void {
    const ctx = this.ctx;
    if (!ctx || this.stage !== 'await') return;
    this.idle = 0;
    const cw = ctx.canvas.clientWidth || 1, ch = ctx.canvas.clientHeight || 1;
    this.ndc.set((x / cw) * 2 - 1, -((y / ch) * 2 - 1));
    this.camera.updateMatrixWorld();
    this.caster.setFromCamera(this.ndc, this.camera);
    if (!this.caster.ray.intersectPlane(this.plane, this.hit)) return;
    const dist = Math.hypot(this.hit.x, this.hit.y);
    if (dist < MIN_CLICK_RADIUS) return; // the direction from the hole is undefined right on top of it
    this.placeGuess(Math.atan2(this.hit.y, this.hit.x), Math.min(dist, MAX_MARK_RADIUS));
  }

  /**
   * The keyboard route to the same guess. While S2 waits, left and right turn a mark about the hole (left is
   * counter-clockwise, the way the orbit turns) and Enter places it. Enter also steps the words, so the first press
   * only shows the mark: a reader who is still tapping Enter does not commit a guess they have not seen.
   */
  onKey(key: string): boolean {
    if (this.stage !== 'await') return false;
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      this.idle = 0;
      this.keyAngle += key === 'ArrowLeft' ? KEY_STEP : -KEY_STEP;
      this.showKeyMark();
      return true;
    }
    if (key === 'Enter') {
      if (!this.keyPreview) this.showKeyMark();
      else this.placeGuess(this.keyAngle);
      return true;
    }
    return false;
  }

  /** The accessible route ("Show me"): watch the lap out, put the guess on Einstein's direction, let S2 come back. */
  demonstrate(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (this.stage === 'watch') this.skipToPrediction();
    if (this.stage !== 'await') return;
    this.placeGuess(pericentreDirection(2));
    ctx.hud.setHint('Watch S2 come back to the gold ring.');
  }

  /** The hole and the star, named where they are. Projected with the camera's own matrices, which carry the view offset. */
  labels(width: number, height: number): ScreenLabel[] {
    const out = this.labelList;
    out.length = 0;
    this.camera.updateMatrixWorld();
    // the hint ring has no text and S2 waits far outside it, so both names always have room
    if (this.pinLabel(this.holeLabel, 0, 0, width, height)) out.push(this.holeLabel);
    if (this.pinLabel(this.s2Label, this.s2.position.x, this.s2.position.y, width, height)) out.push(this.s2Label);
    return out;
  }

  /**
   * Everything that follows once the direction of the guess is known. The mark sits markRadius from the hole along
   * the angle; a click shows it where the pointer landed, the keyboard and Show me use the default distance.
   */
  private placeGuess(angle: number, markRadius = KEY_MARK_RADIUS) {
    const ctx = this.ctx;
    if (!ctx || this.stage !== 'await') return;
    const newton = pericentreDirection(1);
    const einstein = pericentreDirection(2);
    this.guessDeg = angularErrorDeg(Math.cos(angle), Math.sin(angle), einstein);

    this.guessMark.position.set(markRadius * Math.cos(angle), markRadius * Math.sin(angle), 0);
    this.newtonMark.position.set(S2_PERICENTRE * Math.cos(newton), S2_PERICENTRE * Math.sin(newton), 0);
    this.einsteinMark.position.set(S2_PERICENTRE * Math.cos(einstein), S2_PERICENTRE * Math.sin(einstein), 0);
    this.guessMark.visible = this.newtonMark.visible = this.einsteinMark.visible = true;

    this.stage = 'resume';
    this.hintRing.visible = false;
    ctx.hud.setLever({ kind: 'buttons', note: 'Watching S2 come back', items: [] });
    ctx.hud.setMove(null);
    ctx.hud.setHint('Ice ring: where it would come back if the orbit closed. Gold ring: where the twist actually puts it.');
    this.refreshReadouts();
  }

  /** Show the keyboard mark at keyAngle, five units from the hole. */
  private showKeyMark() {
    this.keyPreview = true;
    this.guessMark.position.set(KEY_MARK_RADIUS * Math.cos(this.keyAngle), KEY_MARK_RADIUS * Math.sin(this.keyAngle), 0);
    this.guessMark.visible = true;
  }

  /** Project a point on the orbit plane to CSS pixels of the canvas. Returns false when it is behind the camera. */
  private pinLabel(label: ScreenLabel, x: number, y: number, width: number, height: number): boolean {
    const v = this.labelV.set(x, y, 0).project(this.camera);
    if (v.z > 1) return false;
    label.x = ((v.x + 1) / 2) * width;
    label.y = ((1 - v.y) / 2) * height;
    return true;
  }

  /** Free the GPU resources. The director may call this when the whole experience is torn down. */
  dispose(): void {
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose()); else mat?.dispose();
    });
    this.textures.forEach((t) => t.dispose());
  }

  /* ---- orbit ---- */

  /** Advance the mean anomaly, feed the trail, and fire the two story beats. Returns where S2 is now. */
  private stepOrbit(dt: number) {
    if (this.stage === 'await') return s2At(this.phase);

    const from = this.phase;
    let to = from + dt * ORBIT_RATE;
    const lapDone = this.stage === 'watch' && to >= FIRST_LAP_PHASE;
    if (lapDone) to = FIRST_LAP_PHASE;

    // sub-steps, so the trail stays continuous through the fast pericentre pass
    const steps = Math.max(1, Math.ceil((to - from) / SUBSTEP));
    let pushed = false;
    for (let i = 1; i <= steps; i++) {
      const p = s2At(from + ((to - from) * i) / steps);
      if (this.trail.push(p.x, p.y, 0)) pushed = true;
    }
    if (pushed) (this.trailPoints.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    this.phase = to;

    if (lapDone) this.beginPrediction();
    else if (this.stage === 'resume' && from < pericentrePhase(2) && to >= pericentrePhase(2)) this.complete();
    return s2At(this.phase);
  }

  /** Show me from mid-lap: play the rest of the first lap now, in the steps and with the clocks a real lap has. */
  private skipToPrediction() {
    const ctx = this.ctx;
    if (!ctx) return;
    for (let i = 0; i < MAX_SKIP_STEPS && this.stage === 'watch'; i++) {
      const star = this.stepOrbit(SKIP_STEP);
      this.sTime += SKIP_STEP;
      ctx.clock.advance(SKIP_STEP * MISSION_SECONDS_PER_SECOND, SKIP_STEP * MISSION_SECONDS_PER_SECOND * clockRate(star.r));
    }
  }

  /** The first lap is done and S2 waits at apocentre for the player's mark. */
  private beginPrediction() {
    const ctx = this.ctx;
    this.stage = 'await';
    this.keyAngle = pericentreDirection(1);
    this.keyPreview = false;
    if (!ctx) return;
    this.hintRing.visible = true;
    ctx.hud.setLever({ kind: 'buttons', note: 'S2 is waiting for your guess', items: [] });
    ctx.hud.setMove('Click right beside the black hole, inside the gold ring, on the side where you think S2 will swing past closest next lap. Or turn the mark with the left and right arrow keys and press Enter.');
    ctx.hud.setHint('');
    ctx.audio.blip('choice');
  }

  /** S2 has crossed Einstein's mark. */
  private complete() {
    const ctx = this.ctx;
    this.stage = 'done';
    this.pulse = PULSE_SECONDS;
    if (!ctx) return;
    ctx.say(meta.payoff);
    ctx.measure('time', LETTER.measurements.time);
    ctx.audio.blip('ok');
    ctx.hud.setLever(null);
    ctx.hud.setMove(null);
    ctx.hud.setHint('Drag to look around. Scroll to zoom.');
    this.refreshReadouts();
    ctx.finish();
  }

  /* ---- drawing ---- */

  private refreshReadouts() {
    const rows: [string, string][] = [
      ['S2 at closest approach', '7,650 km/s, 2.5% of light'],
      ['Its clock there', '0.9997 of Earth’s'],
      ['Orbit turn per lap', '12 arcminutes, shown 200× larger']
    ];
    if (this.guessDeg !== null) {
      rows.push(['Your guess', `${this.guessDeg.toFixed(0)}° from Einstein’s mark`]);
      rows.push(['Ice ring', 'Newton: the orbit closes']);
      rows.push(['Gold ring', 'Einstein: the orbit turns']);
    }
    this.ctx?.hud.setReadouts(rows);
  }

  /** Put every moving thing where it belongs for the current state. */
  private syncVisuals(star: { x: number; y: number; omega: number }) {
    this.s2.position.set(star.x, star.y, 0);
    const s2Size = 0.35 + 0.018 * this.radius;
    this.s2.scale.set(s2Size, s2Size, 1);
    this.orbitLine.rotation.z = star.omega;
    this.omega = star.omega;

    for (let i = 0; i < this.sStarData.length; i++) {
      const p = sStarPosition(this.sStarData[i], this.sTime);
      this.sStarPositions[i * 3] = p.x; this.sStarPositions[i * 3 + 1] = p.y; this.sStarPositions[i * 3 + 2] = p.z;
    }
    (this.sStars.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;

    this.trailMat.uniforms.uHead.value = this.trail.head;
    this.trailMat.uniforms.uCount.value = this.trail.count;

    const markGain = clamp(this.radius / CAM_RADIUS, 0.75, 1.2);
    const markSize = 0.8 * markGain, guessSize = 1.5 * markGain;
    this.guessMark.scale.set(guessSize, guessSize, 1);
    this.newtonMark.scale.set(markSize, markSize, 1);
    let einstein = markSize;
    if (this.pulse > 0) {
      const k = this.pulse / PULSE_SECONDS;
      einstein *= 1 + 1.3 * k * k;
    }
    this.einsteinMark.scale.set(einstein, einstein, 1);
    if (this.hintRing.visible) { const s = 10 + 0.9 * Math.sin(this.sTime * 3); this.hintRing.scale.set(s, s, 1); }
  }

  /* ---- camera ---- */

  private placeCamera() {
    const ce = Math.cos(this.el);
    const az = this.az + this.azFollow + SWAY_AMPLITUDE * Math.sin(SWAY_RATE * this.swayT);
    this.camera.position.set(this.radius * ce * Math.cos(az), this.radius * ce * Math.sin(az), this.radius * Math.sin(this.el));
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateMatrixWorld();
  }

  private updateCamera(dt: number, width: number, height: number) {
    this.idle += dt;
    if (!this.reduceMotion) this.swayT += dt;
    this.azFollow += (this.omega - this.azFollow) * (1 - Math.exp(-dt / FOLLOW_TAU));
    this.radius += (this.targetRadius - this.radius) * (1 - Math.exp(-dt * 8));
    this.placeCamera();

    if (width > 0 && height > 0 && (width !== this.lastW || height !== this.lastH)) {
      this.lastW = width; this.lastH = height;
      const aspect = width / height;
      const tanHalf = Math.tan((FOV * DEG) / 2) * Math.max(1, FULL_FOV_ASPECT / aspect);
      this.camera.aspect = aspect;
      this.camera.fov = Math.min(MAX_FOV, (2 * Math.atan(tanHalf)) / DEG);
      // setViewOffset rebuilds the projection matrix (and its inverse, which the click unprojection uses)
      const shiftY = height > width * 1.05 ? VIEW_SHIFT_Y + 0.22 : VIEW_SHIFT_Y;   // upright phone: the orbit rides above the captions
      this.camera.setViewOffset(width, height, (-VIEW_SHIFT_X / 2) * width, (shiftY / 2) * height, width, height);
    }
    if (this.lastH > 0) {
      const pxPerUnit = this.lastH / (2 * Math.tan((this.camera.fov * DEG) / 2));
      this.trailMat.uniforms.uPx.value = pxPerUnit;
      // pixel sizes follow the shorter side of a 16:9 frame, so tall phone screens do not inflate the stars
      this.starMat.uniforms.uScale.value = Math.min(this.lastH, (this.lastW * 9) / 16) / 1080;
    }
  }

  private own<T extends THREE.Texture>(tex: T): T {
    this.textures.push(tex);
    return tex;
  }
}
